import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { ArrowRight, Minus, Package, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatKD } from "@/lib/utils";
import { useShopCart } from "@/lib/cart";
import { useProducts } from "@/hooks/use-products";
import { shopBasePath } from "@/lib/shop-market";

export default function Cart() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [, navigate] = useLocation();
  const { items, subtotal, setQuantity, removeItem, b2bKey } = useShopCart();
  // Fresh stock data so quantities get re-capped when stock dropped since adding.
  // B2B items carry their link key; their stock comes from the B2B list.
  const { data: products } = useProducts();
  const { data: b2bProducts } = useProducts(b2bKey);
  const stockOf = (productId: string) =>
    (products?.find((p) => p.id === productId) ?? b2bProducts?.find((p) => p.id === productId))
      ?.stockQuantity;

  if (items.length === 0) {
    return (
      <div className="min-h-screen pt-24 pb-24 flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
          <p className="text-lg font-semibold mb-1">{t("shop.cart.emptyTitle")}</p>
          <p className="text-sm text-muted-foreground mb-6">{t("shop.cart.emptyBody")}</p>
          <Link href={shopBasePath(b2bKey)}>
            <Button className="rounded-full px-8">{t("shop.backToShop")}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-24">
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="text-3xl md:text-4xl font-bold font-display mb-8">
          {t("shop.cart.title")}
        </h1>

        <ul className="space-y-3 mb-8">
          {items.map((item) => {
            const stock = stockOf(item.productId);
            const max = stock !== undefined ? Math.max(0, stock) : 99;
            const overStock = stock !== undefined && item.quantity > stock;
            return (
              <li
                key={item.productId}
                className="flex items-center gap-4 rounded-2xl border border-border bg-card p-3"
                data-testid={`cart-item-${item.sku}`}
              >
                <Link href={`${shopBasePath(item.b2bKey)}/${item.slug}`} className="shrink-0">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={isRtl ? item.nameAr : item.nameEn}
                      className="h-16 w-16 rounded-xl object-cover"
                    />
                  ) : (
                    <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-muted">
                      <Package className="h-6 w-6 text-muted-foreground/40" />
                    </span>
                  )}
                </Link>
                <div className="min-w-0 flex-1">
                  <Link
                    href={`${shopBasePath(item.b2bKey)}/${item.slug}`}
                    className="font-semibold hover:text-primary transition-colors line-clamp-1"
                  >
                    {isRtl ? item.nameAr : item.nameEn}
                  </Link>
                  <p className="text-xs text-muted-foreground font-mono">{item.sku}</p>
                  <p className="text-sm font-semibold text-primary mt-0.5">
                    {formatKD(item.price * item.quantity)}{" "}
                    <span className="text-[10px] text-muted-foreground font-normal">
                      {t("services.currency")}
                    </span>
                  </p>
                  {overStock && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      {t("shop.cart.stockAdjusted", { count: stock })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 rounded-full border border-border">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-full"
                    onClick={() => setQuantity(item.productId, item.quantity - 1)}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <span className="w-6 text-center text-sm font-semibold">{item.quantity}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-full"
                    disabled={item.quantity >= max}
                    onClick={() => setQuantity(item.productId, Math.min(max, item.quantity + 1))}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground"
                  onClick={() => removeItem(item.productId)}
                  data-testid={`cart-remove-${item.sku}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>

        <div className="rounded-2xl border border-border bg-card p-5 space-y-2 mb-6">
          <div className="flex items-center justify-between font-semibold">
            <span>{t("shop.cart.subtotal")}</span>
            <span className="text-primary">
              {formatKD(subtotal)}{" "}
              <span className="text-xs text-muted-foreground font-normal">
                {t("services.currency")}
              </span>
            </span>
          </div>
          <p className="text-xs text-muted-foreground">{t("shop.cart.deliveryNote")}</p>
        </div>

        <Button
          className="w-full h-14 text-lg font-semibold rounded-xl"
          onClick={() => navigate("/checkout")}
          data-testid="cart-checkout"
        >
          {t("shop.cart.checkout")}
          <ArrowRight className={`w-5 h-5 ms-2 ${isRtl ? "rotate-180" : ""}`} />
        </Button>
      </div>
    </div>
  );
}
